<script setup>
// Imports
import { DateTime } from 'luxon'
import { format } from 'quasar'
import { getOpenPaymentWithDiscount, getProductDiscount, getProductSalesPrice, getProductSalesPriceWithDiscount, numberFormat } from 'src/utils/utils.js'

// Constants declarations
const { capitalize, humanStorageSize } = format
const $q = useQuasar()

// Models
const model = defineModel({ type: [String, null], required: true })
const modelPackage = defineModel('package', { type: [Object, null], required: false })

// Stores

// Component props
const componentProps = defineProps({
  webhostingPackages: {
    type: Array,
    required: true,
    default: null,
  },
  subscription: {
    type: [Object, null],
    required: true,
    default: null,
  },
  contract: {
    type: [Object, null],
    required: true,
    default: null,
  },
  maxCols: {
    type: Number,
    required: false,
    default: 4,
    min: 1,
    max: 4,
  },
  smallPadding: {
    type: Boolean,
    required: false,
    default: false,
  },
  alwaysShowButtons: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideUnselectedPackages: {
    type: Boolean,
    required: false,
    default: true,
  },
  cardShadow: {
    type: Boolean,
    required: false,
    default: true,
  },
  showMostChosen: {
    type: Boolean,
    required: false,
    default: false,
  },
})

// Refs
const translationKey = ref('webhosting-packages')

// Computed properties

// Methods

// Import functions

// Constants declarations

// Component props

// Store(s)

// Computed properties
const computedWebhostingPackages = computed(() => {
  if (model.value && componentProps.hideUnselectedPackages) {
    return componentProps.webhostingPackages.filter((hostingPackage) => hostingPackage.productInfo._id === model.value)
  }
  return componentProps.webhostingPackages
})

// Static variables

// Methods
const setModelValues = (productId, hostingPackage) => {
  model.value = productId
  modelPackage.value = hostingPackage
}

// Watchers

// Lifecycle hooks
onBeforeMount(async () => {})
</script>

<template>
  <div class="row">
    <div
      v-for="webhostingPackage in computedWebhostingPackages"
      :key="webhostingPackage._id"
      class="col-12"
      :class="{
        'col-sm-6': maxCols > 1 && webhostingPackages.length > 1 && (!model || !hideUnselectedPackages),
        'col-md-4': maxCols > 2 && webhostingPackages.length > 2 && webhostingPackages.length !== 4 && (!model || !hideUnselectedPackages),
        'col-lg-3': maxCols > 3 && webhostingPackages.length > 3 && webhostingPackages.length !== 5 && (!model || !hideUnselectedPackages),
        'q-pt-sm q-pl-sm': smallPadding,
        'q-pt-md q-pl-md': !smallPadding,
      }"
    >
      <q-card
        class="rounded-borders"
        :class="{ 'card-shadow': cardShadow, 'border-primary': !hideUnselectedPackages && !cardShadow && model === webhostingPackage.productInfo._id }"
        :bordered="!cardShadow"
        flat
      >
        <q-card-section class="q-pb-sm q-px-sm full-width text-center" :class="[showMostChosen ? 'q-pt-xl' : 'q-pt-md']">
          <q-badge v-if="showMostChosen && webhostingPackage.label?.includes('Standard')" floating class="q-pa-sm"
            >{{ $t('Customer.' + capitalize(translationKey) + '.Most chosen') }}
          </q-badge>
          <div class="font-size-rem-13 flex flex-center">
            <q-icon name="o_check" class="text-primary q-mr-sm" size="25px" v-if="model === webhostingPackage.productInfo._id" />
            {{ webhostingPackage.label }}
          </div>
          <div class="font-size-rem-12 q-mt-md text-primary font-size-rem-20">
            {{
              numberFormat(
                useUserStore().currentRegionalSettings.lang,
                getProductSalesPriceWithDiscount(
                  subscription,
                  contract,
                  getProductSalesPrice(webhostingPackage.productInfo?.salesPrices, 'year', 1),
                  webhostingPackage.productInfo._id,
                ),
                true,
              )
            }}
          </div>
          <div class="text-primary q-mt-sm">{{ $t('Customer.' + capitalize(translationKey) + '.per year') }}</div>
          <div class="text-primary">
            &nbsp;
            <template
              v-if="
                getProductDiscount(
                  subscription,
                  contract,
                  getProductSalesPrice(webhostingPackage.productInfo?.salesPrices, 'year', 1),
                  webhostingPackage.productInfo._id,
                )
              "
            >
              {{ $t('Customer.' + capitalize(translationKey) + '.incl') }}
              {{
                numberFormat(
                  useUserStore().currentRegionalSettings.lang,
                  getProductDiscount(
                    subscription,
                    contract,
                    getProductSalesPrice(webhostingPackage.productInfo?.salesPrices, 'year', 1),
                    webhostingPackage.productInfo._id,
                  ),
                  true,
                )
              }}
              {{ $t('Customer.' + capitalize(translationKey) + '.discount') }}
            </template>
          </div>
          <div style="height: 75px">
            &nbsp;
            <q-btn
              v-if="!model || alwaysShowButtons"
              :disable="model === webhostingPackage.productInfo._id"
              color="primary"
              :label="$t('Customer.' + capitalize(translationKey) + '.Choose package')"
              class="full-width q-mt-sm"
              @click="setModelValues(webhostingPackage.productInfo._id, webhostingPackage)"
            />
            <div v-else-if="subscription">
              <div>
                {{ $t('Customer.' + capitalize(translationKey) + '.Amount due by') }}
                <!-- eslint-disable-next-line -->
                {{
                  subscription.invoicedUntilDate
                    ? $d(subscription.invoicedUntilDate, 'short', useUserStore().currentRegionalSettings.lang)
                    : $d(DateTime.fromISO(subscription.invoiceStartDate).plus({ years: 1 }).toISODate(), 'short', useUserStore().currentRegionalSettings.lang)
                }}
              </div>
              <div class="q-mt-sm font-size-rem-13">
                {{
                  numberFormat(
                    useUserStore().currentRegionalSettings.lang,
                    subscription.invoicedUntilDate
                      ? getOpenPaymentWithDiscount(
                          subscription,
                          contract,
                          getProductSalesPrice(webhostingPackage.productInfo?.salesPrices, 'year', 1),
                          webhostingPackage.productInfo._id,
                        )
                      : getProductSalesPriceWithDiscount(
                          subscription,
                          contract,
                          getProductSalesPrice(webhostingPackage.productInfo?.salesPrices, 'year', 1),
                          webhostingPackage.productInfo._id,
                        ),
                    true,
                  )
                }}
              </div>
            </div>
          </div>
          <div class="text-left q-ml-sm q-mb-sm q-mt-lg">
            <div class="q-mt-sm">
              <q-icon name="sym_o_hard_drive" class="text-primary q-mr-sm" size="22px" />
              {{ humanStorageSize(webhostingPackage.diskspaceMB * 1024 * 1024, 0) }}
              {{ $t('Customer.' + capitalize(translationKey) + '.diskspace') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="network_check" class="text-primary q-mr-sm" size="22px" />
              {{ humanStorageSize(webhostingPackage.bandwidthMB * 1024 * 1024, 0) }}
              {{ $t('Customer.' + capitalize(translationKey) + '.bandwidth') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="sym_o_support_agent" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Support included') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="sym_o_backup" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.30 days backup') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="sym_o_touch_app" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.One-Click WordPress installation') }}
            </div>
            <div class="q-mt-md text-weight-bolder">
              {{ $t('Customer.' + capitalize(translationKey) + '.Included features') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="o_check" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Free SSL certificates') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="o_check" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Unlimited domain links') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="o_check" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Unlimited subdomains') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="o_check" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Unlimited mailboxes') }}
            </div>
            <div class="q-mt-sm">
              <q-icon name="o_check" class="text-primary q-mr-sm" size="22px" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Unlimited databases') }}
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>
  </div>
</template>

<style scoped></style>
